// <pos-customer-reservation-manage/> — 客人自助管理訂位（確認出席 / 取消）
// 路徑：#/rsv/:token（token = HMAC 簽名連結，來自確認信 / 提醒信 / 訂位成功頁）
// ?action=confirm 直達確認（email 按鈕用）
// see netlify/functions/reservation-manage.js

import { apiUrl } from '../lib/api-base.js';
import { t, lang } from '../lib/i18n.js';

function parseToken() {
  const m = location.hash.match(/^#\/rsv\/([^\/?&]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

function parseAction() {
  const q = location.hash.split('?')[1];
  return new URLSearchParams(q || '').get('action');
}

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

const STATUS_LABELS = () => ({
  pending: t('待確認'),
  confirmed: t('已確認'),
  cancelled: t('已取消'),
  completed: t('已完成'),
  no_show: t('未到'),
  seated: t('已就座'),
});

class PosCustomerReservationManage extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.state = { loading: true, error: null, reservation: null, shopName: '', busy: false, done: null, armCancel: false };
  }

  async connectedCallback() {
    this.render();
    await this.load();
    // email 的「確認出席」按鈕直達：載入成功且可操作 → 自動執行 confirm
    if (parseAction() === 'confirm' && this.state.reservation
        && ['pending', 'confirmed'].includes(this.state.reservation.status)
        && new Date(this.state.reservation.reserved_at) > new Date()) {
      await this.act('confirm');
    }
  }

  async load() {
    const token = parseToken();
    if (!token) {
      this.state.loading = false;
      this.state.error = t('連結無效');
      this.render();
      return;
    }
    try {
      const res = await fetch(apiUrl(`/.netlify/functions/reservation-manage?token=${encodeURIComponent(token)}`));
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw data?.error || {};
      this.state.reservation = data.reservation;
      this.state.shopName = data.shop_name || '';
    } catch (err) {
      this.state.error = (lang() === 'zh' ? err.message_zh : err.message_en) || err.message_zh || t('連結無效');
    } finally {
      this.state.loading = false;
      this.render();
    }
  }

  async act(action) {
    if (this.state.busy) return;
    // in-app browser（Gmail/LINE 內建瀏覽器）會吃掉原生 confirm() → 改頁內兩段式確認
    if (action === 'cancel' && !this.state.armCancel) {
      this.state.armCancel = true;
      this.render();
      clearTimeout(this._armTimer);
      this._armTimer = setTimeout(() => { this.state.armCancel = false; this.render(); }, 6000);
      return;
    }
    clearTimeout(this._armTimer);
    this.state.armCancel = false;
    this.state.busy = true;
    this.render();
    try {
      const res = await fetch(apiUrl('/.netlify/functions/reservation-manage'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: parseToken(), action }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw data?.error || {};
      this.state.reservation = { ...this.state.reservation, ...data.reservation };
      this.state.done = action;
    } catch (err) {
      this.state.error = (lang() === 'zh' ? err.message_zh : err.message_en) || t('操作失敗，請重試');
    } finally {
      this.state.busy = false;
      this.render();
    }
  }

  render() {
    const { loading, error, reservation, shopName, busy, done } = this.state;

    if (loading) {
      this.shadowRoot.innerHTML = `<style>${this.styles()}</style><div class="msg">${t('載入中…')}</div>`;
      return;
    }
    if (error && !reservation) {
      this.shadowRoot.innerHTML = `<style>${this.styles()}</style>
        <div class="card"><div class="icon icon--warn">!</div><h2>${escapeHtml(error)}</h2>
        <p class="hint">${t('如需協助請直接致電店家')}</p></div>`;
      return;
    }

    const r = reservation;
    const dt = new Date(r.reserved_at);
    const dtLabel = dt.toLocaleString(lang() === 'zh' ? 'zh-TW' : 'en-US', {
      timeZone: 'Asia/Taipei', month: 'numeric', day: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false, weekday: 'short',
    });
    const actionable = ['pending', 'confirmed'].includes(r.status) && dt > new Date();
    const statusLabel = STATUS_LABELS()[r.status] || r.status;

    this.shadowRoot.innerHTML = `
      <style>${this.styles()}</style>
      <div class="card">
        ${done === 'cancel' || r.status === 'cancelled'
          ? `<div class="icon icon--muted">✕</div><h2>${t('訂位已取消')}</h2>`
          : done === 'confirm'
            ? `<div class="icon">✓</div><h2>${t('已確認出席，到時見！')}</h2>`
            : `<div class="icon">📅</div><h2>${t('我的訂位')}</h2>`}

        <div class="row"><span>${t('店家')}</span><strong>${escapeHtml(shopName)}</strong></div>
        <div class="row"><span>${t('時間')}</span><strong>${dtLabel}</strong></div>
        <div class="row"><span>${t('人數')}</span><strong>${t('{n} 位', { n: r.party_size })}</strong></div>
        <div class="row"><span>${t('姓名')}</span><strong>${escapeHtml(r.guest_name || '')}</strong></div>
        <div class="row"><span>${t('狀態')}</span><strong>${escapeHtml(statusLabel)}</strong></div>

        ${error ? `<div class="alert">${escapeHtml(error)}</div>` : ''}

        ${actionable && r.status !== 'cancelled' ? `
          <div class="actions">
            ${done !== 'confirm' ? `<button class="btn btn--primary" data-act="confirm" ${busy ? 'disabled' : ''}>${busy ? '…' : `✓ ${t('確認出席')}`}</button>` : ''}
            <button class="btn btn--danger ${this.state.armCancel ? 'btn--armed' : ''}" data-act="cancel" ${busy ? 'disabled' : ''}>${busy ? '…' : this.state.armCancel ? t('確定取消？再按一次') : t('取消訂位')}</button>
          </div>
          <div class="policy">⏱ ${t('訂位保留 10 分鐘，逾時恕不保留座位')}</div>
        ` : `<p class="hint">${t('如需協助請直接致電店家')}</p>`}
      </div>
    `;

    this.shadowRoot.querySelectorAll('[data-act]').forEach((b) =>
      b.addEventListener('click', () => this.act(b.dataset.act)));
  }

  styles() {
    return `
      :host { display: block; min-height: 100vh; background: #faf8f4; font-family: -apple-system, BlinkMacSystemFont, 'PingFang TC', sans-serif; color: #2a2a2a; }
      .msg { padding: 4rem 1rem; text-align: center; color: #666; }
      .card { max-width: 420px; margin: 0 auto; padding: 2.5rem 1.5rem; text-align: center; }
      .icon { width: 64px; height: 64px; border-radius: 50%; background: #c97b4f; color: #fff; display: inline-flex; align-items: center; justify-content: center; font-size: 2rem; margin-bottom: 1rem; }
      .icon--warn { background: #e0a800; }
      .icon--muted { background: #999; }
      h2 { margin: 0 0 1.25rem; font-size: 1.3rem; }
      .row { display: flex; justify-content: space-between; padding: 0.7rem 0; border-bottom: 1px solid #eee; font-size: 0.95rem; text-align: left; }
      .row span { color: #888; }
      .alert { background: #fff3cd; color: #856404; padding: 0.7rem 1rem; border-radius: 8px; margin-top: 1rem; font-size: 0.9rem; }
      .actions { display: flex; gap: 0.75rem; margin-top: 1.5rem; }
      .btn { flex: 1; padding: 0.9rem; border-radius: 12px; font-size: 1rem; font-weight: 600; cursor: pointer; border: 1px solid #ddd; background: #fff; font-family: inherit; }
      .btn--primary { background: #c97b4f; color: #fff; border-color: #c97b4f; }
      .btn--danger { color: #c0392b; border-color: #c0392b; background: #fff; }
      .btn--armed { background: #c0392b; color: #fff; font-weight: 700; }
      .btn:disabled { opacity: 0.5; cursor: wait; }
      .policy { margin-top: 1.25rem; padding: 0.65rem 0.9rem; background: #fdf2ea; color: #a15c2f; border-radius: 8px; font-size: 0.85rem; }
      .hint { color: #888; font-size: 0.88rem; margin-top: 1.25rem; }
    `;
  }
}

if (!customElements.get('pos-customer-reservation-manage')) {
  customElements.define('pos-customer-reservation-manage', PosCustomerReservationManage);
}
